import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { manufacturingApi, type ManufacturingOrderDto } from '../api/manufacturing';
import { FeatureGate } from '../components/FeatureGate';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const STATUS_BADGE_CLASS: Record<string, string> = {
  draft: 'badge-draft',
  in_progress: 'badge-unpaid',
  completed: 'badge-green',
  cancelled: 'badge-overdue',
};

const fmtQty = (n?: number) => (n !== undefined ? n.toLocaleString('en-IN', { maximumFractionDigits: 3 }) : '—');

export default function ManufacturingPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [newOrderForm, setNewOrderForm] = useState({
    bomId: '',
    plannedQty: 1,
    startDate: '',
    endDate: '',
    notes: '',
  });
  const [orderError, setOrderError] = useState<string | null>(null);
  const [completeModal, setCompleteModal] = useState<ManufacturingOrderDto | null>(null);
  const [completeForm, setCompleteForm] = useState({ actualQty: 0, actualCost: 0, notes: '' });
  const [completeCostTouched, setCompleteCostTouched] = useState(false);
  const [editModal, setEditModal] = useState<ManufacturingOrderDto | null>(null);
  const [editForm, setEditForm] = useState({ actualQty: 0, actualCost: 0, notes: '' });
  const [editCostTouched, setEditCostTouched] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Suggest (not force) Actual Total Cost from the BOM's calculated cost — reused from the
  // same endpoint BomEditorPage's own cost calculator calls. Only one of the two modals is
  // ever open at a time.
  const activeModalOrder = completeModal ?? editModal;
  const activeQty = completeModal ? completeForm.actualQty : editForm.actualQty;
  const { data: bomCostSuggestion } = useQuery({
    queryKey: ['bom-cost-suggestion', activeModalOrder?.bomId, activeQty],
    queryFn: () => manufacturingApi.calcBomCost(activeModalOrder!.bomId, activeQty),
    enabled: !!activeModalOrder && activeQty > 0,
    staleTime: 10_000,
  });
  const suggestedCost = Math.round((bomCostSuggestion as any)?.cost?.totalCost ?? 0);

  useEffect(() => {
    if (!bomCostSuggestion) return;
    if (completeModal && !completeCostTouched) setCompleteForm((f) => ({ ...f, actualCost: suggestedCost }));
    if (editModal && !editCostTouched) setEditForm((f) => ({ ...f, actualCost: suggestedCost }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bomCostSuggestion]);

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['manufacturing-orders', statusFilter],
    queryFn: () => manufacturingApi.listOrders(statusFilter || undefined),
    staleTime: 30_000,
  });

  const { data: boms = [] } = useQuery({
    queryKey: ['boms'],
    queryFn: manufacturingApi.listBoms,
    staleTime: 60_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['manufacturing-orders'] });

  const createOrder = useMutation({
    mutationFn: manufacturingApi.createOrder,
    onSuccess: () => { setShowNewOrder(false); setNewOrderForm({ bomId: '', plannedQty: 1, startDate: '', endDate: '', notes: '' }); setOrderError(null); invalidate(); qc.refetchQueries({ type: 'all' }); },
    onError: (e: any) => setOrderError(e?.response?.data?.message ?? e.message),
  });

  const startOrder = useMutation({
    mutationFn: (id: string) => manufacturingApi.startOrder(id),
    onSuccess: () => { setActionError(null); invalidate(); qc.invalidateQueries({ queryKey: ['products'] }); qc.refetchQueries({ type: 'all' }); },
    onError: (e: any) => setActionError(e?.response?.data?.message ?? e.message),
  });

  const completeOrder = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: { actualQty: number; actualCost?: number; notes?: string } }) =>
      manufacturingApi.completeOrder(id, dto),
    onSuccess: () => { setCompleteModal(null); setActionError(null); invalidate(); qc.invalidateQueries({ queryKey: ['products'] }); qc.refetchQueries({ type: 'all' }); },
    onError: (e: any) => setActionError(e?.response?.data?.message ?? e.message),
  });

  const cancelOrder = useMutation({
    mutationFn: (id: string) => manufacturingApi.cancelOrder(id),
    onSuccess: () => { setActionError(null); invalidate(); qc.invalidateQueries({ queryKey: ['products'] }); qc.refetchQueries({ type: 'all' }); },
    onError: (e: any) => setActionError(e?.response?.data?.message ?? e.message),
  });

  const deleteOrder = useMutation({
    mutationFn: (id: string) => manufacturingApi.deleteOrder(id),
    onSuccess: () => { setActionError(null); invalidate(); qc.invalidateQueries({ queryKey: ['products'] }); qc.refetchQueries({ type: 'all' }); },
    onError: (e: any) => setActionError(e?.response?.data?.message ?? e.message),
  });

  const editCompletion = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: { actualQty: number; actualCost?: number; notes?: string } }) =>
      manufacturingApi.editCompletion(id, dto),
    onSuccess: () => { setEditModal(null); setActionError(null); invalidate(); qc.invalidateQueries({ queryKey: ['products'] }); qc.refetchQueries({ type: 'all' }); },
    onError: (e: any) => setActionError(e?.response?.data?.message ?? e.message),
  });

  return (
    <FeatureGate feature="manufacturingModule" requiredPlan="Gold" upgradeMessage="Manufacturing module requires a Gold plan.">
      <div style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Manufacturing</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/manufacturing/boms')}>
              BOM Editor
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowNewOrder(true)}>
              + New Production Order
            </button>
          </div>
        </div>

        {/* Status filter tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {['', 'draft', 'in_progress', 'completed', 'cancelled'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '9999px',
                border: '1px solid',
                borderColor: statusFilter === s ? 'var(--primary)' : 'var(--border-input)',
                background: statusFilter === s ? 'var(--primary)' : 'transparent',
                color: statusFilter === s ? 'white' : 'var(--text-2)',
                cursor: 'pointer',
                fontSize: '0.8rem',
              }}
            >
              {s === '' ? 'All' : STATUS_LABEL[s]}
            </button>
          ))}
        </div>

        {actionError && (
          <div className="error-box" style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
            {actionError}
          </div>
        )}

        {isLoading ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading…</p>
        ) : orders.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No manufacturing orders found.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {orders.map((order: ManufacturingOrderDto) => {
              const product = order.bom?.product;
              const pct = order.plannedQty > 0 ? Math.round((order.completedQty / order.plannedQty) * 100) : 0;
              return (
                <div key={order.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{order.orderNumber}</span>
                      <span style={{ marginLeft: '0.75rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                        {product?.name ?? 'Unknown product'}
                      </span>
                    </div>
                    <span className={`badge ${STATUS_BADGE_CLASS[order.status]}`} style={{ fontSize: '0.78rem', padding: '0.2rem 0.6rem' }}>
                      {STATUS_LABEL[order.status]}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '2rem', marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-2)', flexWrap: 'wrap' }}>
                    <span>Planned: {fmtQty(order.plannedQty)} {product?.unit ?? ''}</span>
                    <span>Completed: {fmtQty(order.completedQty)} {product?.unit ?? ''}</span>
                    {order.startDate && <span>Start: {order.startDate.slice(0, 10)}</span>}
                    {order.endDate && <span>End: {order.endDate.slice(0, 10)}</span>}
                  </div>

                  {order.status === 'in_progress' && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <div style={{ background: 'var(--border)', borderRadius: '9999px', height: '6px', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, background: 'var(--primary)', height: '100%' }} />
                      </div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{pct}% complete</span>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                    {order.status === 'draft' && (
                      <>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => startOrder.mutate(order.id)}
                          disabled={startOrder.isPending}
                        >
                          Start
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => { if (window.confirm('Cancel this order?')) cancelOrder.mutate(order.id); }}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    {order.status === 'in_progress' && (
                      <>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => { setCompleteModal(order); setCompleteForm({ actualQty: order.plannedQty, actualCost: 0, notes: '' }); setCompleteCostTouched(false); }}
                        >
                          Complete
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => { if (window.confirm('Cancel and reverse stock?')) cancelOrder.mutate(order.id); }}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    {order.status === 'cancelled' && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => { if (window.confirm(`Permanently delete ${order.orderNumber}? This cannot be undone.`)) deleteOrder.mutate(order.id); }}
                        disabled={deleteOrder.isPending}
                      >
                        Delete
                      </button>
                    )}
                    {order.status === 'completed' && (
                      <>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            const batch = order.batches?.[0];
                            setEditModal(order);
                            setEditForm({ actualQty: batch?.actualQty ?? order.completedQty, actualCost: batch?.actualCost ?? 0, notes: batch?.notes ?? '' });
                            // A genuine recorded cost is real data, not a placeholder — protect it from
                            // being silently overwritten by the BOM suggestion the instant the modal opens.
                            setEditCostTouched(!!batch?.actualCost);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => { if (window.confirm(`Delete ${order.orderNumber}? This reverses both the finished-goods stock it added AND the raw materials it consumed. This cannot be undone.`)) deleteOrder.mutate(order.id); }}
                          disabled={deleteOrder.isPending}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* New Order Modal */}
        {showNewOrder && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
            <div style={{ background: 'var(--surface)', borderRadius: '10px', padding: '1.5rem', width: '100%', maxWidth: '480px' }}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>New Production Order</h3>

              {orderError && (
                <div className="error-box" style={{ padding: '0.6rem', fontSize: '0.85rem' }}>
                  {orderError}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Bill of Materials *</label>
                  <select
                    value={newOrderForm.bomId}
                    onChange={(e) => setNewOrderForm((f) => ({ ...f, bomId: e.target.value }))}
                    style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem' }}
                  >
                    <option value="">— Select BOM —</option>
                    {boms.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.product?.name} (v{b.version})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Planned Qty *</label>
                  <input
                    type="number"
                    min="0.001"
                    step="0.001"
                    value={newOrderForm.plannedQty}
                    onChange={(e) => setNewOrderForm((f) => ({ ...f, plannedQty: Number(e.target.value) }))}
                    style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem' }}
                  />
                </div>

                <div className="form-row form-row-2" style={{ gap: '0.5rem', marginBottom: 0 }}>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Start Date</label>
                    <input type="date" value={newOrderForm.startDate} onChange={(e) => setNewOrderForm((f) => ({ ...f, startDate: e.target.value }))} style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>End Date</label>
                    <input type="date" value={newOrderForm.endDate} onChange={(e) => setNewOrderForm((f) => ({ ...f, endDate: e.target.value }))} style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem' }} />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Notes</label>
                  <textarea
                    value={newOrderForm.notes}
                    onChange={(e) => setNewOrderForm((f) => ({ ...f, notes: e.target.value }))}
                    rows={2}
                    style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem', resize: 'vertical' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setShowNewOrder(false); setOrderError(null); }}>Cancel</button>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={!newOrderForm.bomId || !newOrderForm.plannedQty || createOrder.isPending}
                  onClick={() => createOrder.mutate(newOrderForm)}
                >
                  {createOrder.isPending ? 'Creating…' : 'Create Order'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Complete Order Modal */}
        {completeModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
            <div style={{ background: 'var(--surface)', borderRadius: '10px', padding: '1.5rem', width: '100%', maxWidth: '400px' }}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>Complete Order — {completeModal.orderNumber}</h3>

              {actionError && (
                <div className="error-box" style={{ padding: '0.6rem', fontSize: '0.85rem' }}>
                  {actionError}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Actual Qty Produced *</label>
                  <input
                    type="number"
                    min="0.001"
                    step="0.001"
                    value={completeForm.actualQty}
                    onChange={(e) => setCompleteForm((f) => ({ ...f, actualQty: Number(e.target.value) }))}
                    style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Actual Total Cost (₹)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={completeForm.actualCost}
                    onChange={(e) => { setCompleteForm((f) => ({ ...f, actualCost: Number(e.target.value) })); setCompleteCostTouched(true); }}
                    style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem' }}
                  />
                  {!completeCostTouched && suggestedCost > 0 && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Suggested from BOM — edit if this run's real cost differed</span>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Notes</label>
                  <textarea
                    value={completeForm.notes}
                    onChange={(e) => setCompleteForm((f) => ({ ...f, notes: e.target.value }))}
                    rows={2}
                    style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem', resize: 'vertical' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setCompleteModal(null); setActionError(null); }}>Cancel</button>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={!completeForm.actualQty || completeOrder.isPending}
                  onClick={() =>
                    completeOrder.mutate({
                      id: completeModal.id,
                      dto: { actualQty: completeForm.actualQty, actualCost: completeForm.actualCost || undefined, notes: completeForm.notes || undefined },
                    })
                  }
                >
                  {completeOrder.isPending ? 'Saving…' : 'Mark Complete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Completion Modal */}
        {editModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
            <div style={{ background: 'var(--surface)', borderRadius: '10px', padding: '1.5rem', width: '100%', maxWidth: '400px' }}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>Edit Completion — {editModal.orderNumber}</h3>

              {actionError && (
                <div className="error-box" style={{ padding: '0.6rem', fontSize: '0.85rem' }}>
                  {actionError}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Actual Qty Produced *</label>
                  <input
                    type="number"
                    min="0.001"
                    step="0.001"
                    value={editForm.actualQty}
                    onChange={(e) => setEditForm((f) => ({ ...f, actualQty: Number(e.target.value) }))}
                    style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Actual Total Cost (₹)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.actualCost}
                    onChange={(e) => { setEditForm((f) => ({ ...f, actualCost: Number(e.target.value) })); setEditCostTouched(true); }}
                    style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem' }}
                  />
                  {!editCostTouched && suggestedCost > 0 && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Suggested from BOM — edit if this run's real cost differed</span>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>Notes</label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                    rows={2}
                    style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid var(--border-input)', borderRadius: '6px', fontSize: '0.875rem', resize: 'vertical' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setEditModal(null); setActionError(null); }}>Cancel</button>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={!editForm.actualQty || editCompletion.isPending}
                  onClick={() =>
                    editCompletion.mutate({
                      id: editModal.id,
                      dto: { actualQty: editForm.actualQty, actualCost: editForm.actualCost || undefined, notes: editForm.notes || undefined },
                    })
                  }
                >
                  {editCompletion.isPending ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </FeatureGate>
  );
}
